import { useCallback, useMemo, useState } from "react";
import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import CloudDone from "@mui/icons-material/CloudDone";
import CloudOff from "@mui/icons-material/CloudOff";
import CloudQueue from "@mui/icons-material/CloudQueue";
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  Fab,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { debounce } from "lodash-es";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";

import { Button } from "~/components/design-system/index.js";

type useSoadNotesDrawerProps = {
  initialNotes: string;
  onSave: (notes: string) => Promise<void>;
};

export const useSoadNotesDrawer = ({
  initialNotes,
  onSave,
}: useSoadNotesDrawerProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(initialNotes);
  const width = open ? "400px" : "0px";

  return {
    open,
    setOpen,

    /**
     * Sx styles which can be applied to the sibling component of the drawer to
     * adjust it's width when the drawer is open.
     */
    siblingSx: {
      width: `calc(100% - ${width})`,
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },

    /** The component to mount. This will dictate the position of the Fab */
    Component: (
      <SoadNotesDrawer
        open={open}
        setOpen={setOpen}
        notes={notes}
        setNotes={setNotes}
        onSave={onSave}
      />
    ),
  };
};

type SoadNotesDrawerProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  notes: string;
  setNotes: (notes: string) => void;
  onSave: useSoadNotesDrawerProps["onSave"];
};

const SoadNotesDrawer = ({
  open,
  setOpen,
  notes,
  setNotes,
  onSave,
}: SoadNotesDrawerProps) => {
  const { t } = useTranslation();
  const [saveState, setSaveState] = useState<
    "clean" | "dirty" | "saving" | "save-success" | "save-failed"
  >("clean");

  const handleSave = useCallback(
    async (notes: string) => {
      setSaveState("saving");
      try {
        await onSave(notes);
        setSaveState("save-success");
      } catch {
        setSaveState("save-failed");
      }
    },
    [onSave],
  );
  const handleSaveDebounced = useCallback(debounce(handleSave, 1_000), [
    handleSave,
  ]);

  const saveFeedbackIcon = useMemo(
    () =>
      match(saveState)
        .with("dirty", () => <CloudQueue />)
        .with("saving", () => (
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              justifyItems: "center",
            }}
          >
            <CloudQueue sx={{ gridColumnStart: "1", gridRowStart: "1" }} />
            <CircularProgress
              size={8}
              sx={{ gridColumnStart: "1", gridRowStart: "1" }}
            />
          </Box>
        ))
        .with("clean", "save-success", () => <CloudDone color="success" />)
        .with("save-failed", () => <CloudOff color="error" />)
        .exhaustive(),
    [saveState],
  );

  return (
    <Box>
      <Fab
        color="primary"
        variant="extended"
        size="small"
        onClick={() => {
          setOpen(!open);
        }}
      >
        <Stack
          direction="row"
          sx={{
            marginLeft: 1,
            gap: 1,
            alignItems: "center",
          }}
        >
          SOAD Notes
          {open ? <Close /> : <Add />}
        </Stack>
      </Fab>
      <Drawer open={open} variant="persistent" anchor="right">
        <Box sx={{ width: "400px" }}>
          <Stack>
            <Stack
              direction={"row"}
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                padding: 2,
              }}
            >
              <Typography variant={"h5"}>
                {t("pages.soadAssessment.notes.heading")}
              </Typography>
              <IconButton onClick={() => setOpen(false)}>
                <Close />
              </IconButton>
            </Stack>
            <Divider />
            <Stack
              spacing={2}
              sx={{
                padding: 2,
              }}
            >
              <Typography variant={"body1"}>
                {t("pages.soadAssessment.notes.subHeading")}
              </Typography>
              <TextField
                sx={{ flexGrow: 1 }}
                multiline
                minRows={10}
                name="notes"
                value={notes}
                onChange={(event) => {
                  const newNotes = event.target.value;
                  setNotes(newNotes);
                  setSaveState("dirty");
                  void handleSaveDebounced(newNotes);
                }}
              />

              <Stack
                direction={"row"}
                spacing={1}
                sx={{
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                {saveFeedbackIcon}
                <Button
                  label={t("buttonLabels.save")}
                  disabled={saveState === "saving"}
                  onClick={() => {
                    void handleSave(notes);
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
};
